import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  DashboardSlaSlot,
  dashboardSlaSlotForViewName,
  OutboundSystemView,
  StaticSystemViews,
} from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { viewTypes } from "@redotech/redo-views-common/views";
import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { RedoOutboundLabelsRpcClientContext } from "./outbound-labels-rpc-client";
import {
  findDefaultOutboundView,
  OutboundViewsContext,
} from "./outbound-views";

export const ActiveOutboundViewContext = createContext<
  viewTypes["fulfillment-groups"]
>(StaticSystemViews[OutboundSystemView.CREATE]);

export const SetEditingOutboundViewContext = createContext<
  Dispatch<SetStateAction<boolean>>
>(() => {});
export const EditingOutboundViewContext = createContext<boolean>(false);

export interface ActiveDashboardSlaSlot {
  slot: DashboardSlaSlot;
  reload(): void;
}

export const ActiveDashboardSlaSlotContext = createContext<
  ActiveDashboardSlaSlot | undefined
>(undefined);

export const OutboundActiveViewContextProvider = memo(
  function OutboundActiveViewContextProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const viewsContext = useRequiredContext(OutboundViewsContext);
    const viewName = useParams().viewName;

    const rpcClient = useContext(RedoOutboundLabelsRpcClientContext);

    const [editingView, setEditingView] = useState<boolean>(false);

    const dashboardSlaSlot = dashboardSlaSlotForViewName(viewName);
    const [dashboardSlaVersion, setDashboardSlaVersion] = useState(0);
    const dashboardSlaViewLoad = useLoad(
      async (signal) => {
        if (!dashboardSlaSlot || !rpcClient) {
          return null;
        }
        const { slots } = await rpcClient.getDashboardSlaViews(
          { includeCounts: false },
          { signal },
        );
        return slots.find((entry) => entry.slot === dashboardSlaSlot)?.view;
      },
      [rpcClient, dashboardSlaSlot, dashboardSlaVersion],
    );
    const reloadDashboardSlaView = useHandler(() =>
      setDashboardSlaVersion((version) => version + 1),
    );
    const activeDashboardSlaSlot = useMemo(
      () =>
        dashboardSlaSlot
          ? { slot: dashboardSlaSlot, reload: reloadDashboardSlaView }
          : undefined,
      [dashboardSlaSlot, reloadDashboardSlaView],
    );

    const currentView = useMemo(() => {
      if (dashboardSlaSlot) {
        return dashboardSlaViewLoad.value ?? null;
      }
      // If we have yet to load the views, don't show an active view (we specifically don't want to show the create view). If we show a view and then the context loads, we will re-render and show a gross flash of the create view.
      if (!viewsContext || !viewsContext.hasInitallyLoaded) {
        return null;
      }

      const view = viewsContext.views?.find((view) => view.name === viewName);
      if (view) {
        return view;
      }
      // Explicit "create" URL still drops the user into the create-view
      // editor — that's the entry point for "+ Add new view".
      if (viewName === OutboundSystemView.CREATE) {
        return StaticSystemViews[OutboundSystemView.CREATE];
      }
      // Any other unmatched view name (e.g. a stale link to
      // "awaiting-shipment" on a team that has renamed or replaced that
      // system view) falls back to the first view in the team's nav order
      // rather than dumping the user into the create editor.
      const fallback = findDefaultOutboundView({
        views: viewsContext.views ?? [],
        folders: viewsContext.folders ?? [],
      });
      return fallback ?? StaticSystemViews[OutboundSystemView.CREATE];
    }, [viewsContext, viewName, dashboardSlaSlot, dashboardSlaViewLoad.value]);

    useEffect(() => {
      if (currentView?.name === OutboundSystemView.CREATE) {
        setEditingView(true);
      } else {
        setEditingView(false);
      }
    }, [currentView]);

    if (!currentView) {
      return null;
    }

    return (
      <EditingOutboundViewContext.Provider value={editingView}>
        <SetEditingOutboundViewContext.Provider value={setEditingView}>
          <ActiveOutboundViewContext.Provider value={currentView}>
            <ActiveDashboardSlaSlotContext.Provider
              value={activeDashboardSlaSlot}
            >
              {children}
            </ActiveDashboardSlaSlotContext.Provider>
          </ActiveOutboundViewContext.Provider>
        </SetEditingOutboundViewContext.Provider>
      </EditingOutboundViewContext.Provider>
    );
  },
);
